import React, { useCallback, useEffect, useState } from 'react';
import WarningIcon from '../Icons/WarningIcon';
import { InnerViewProps, UserGuideType } from './types';
import { getUserGuides } from './utils';

const HelpAttachmentLanding: React.FC<InnerViewProps> = ({
  setCurrentView,
  appData,
}) => {
  const [search, setSearch] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchResults, setSearchResults] = useState<UserGuideType[]>([]);

  const getGuides = useCallback(async () => {
    const userGuides = await getUserGuides(appData.appToken, search);
    setSearchResults(userGuides);
  }, [search, appData.appToken]);

  useEffect(() => {
    if (search.length > 2) {
      getGuides();
    } else {
      setSearchResults([]);
    }
  }, [search, getGuides]);

  return (
    <>
      <div className="bg-gradient-to-b from-primary to-primary-dark p-5">
        <img
          src={appData.logoPath}
          className="max-h-[45px] mb-6"
          alt={`${appData.appName} Logo`}
        />
        <h3 className="text-2xl font-medium">
          Welcome to the {appData.appName} assistant
        </h3>
        <p className="text-sm text-light text-gray-200">
          Select an option below to get started
        </p>
        <div className="flex items-center justify-start mt-4">
          <button
            className="bg-gray-400 rounded-full px-8 py-2 text-sm font-medium mr-1 shadow-sm hover:bg-green-700 bg-opacity-25"
            onClick={() => setCurrentView('guides')}
          >
            Guides
          </button>
          <button
            className="bg-gray-400 rounded-full px-8 py-2 text-sm font-medium ml-1 shadow-sm hover:bg-green-700 bg-opacity-25"
            onClick={() => setCurrentView('contact')}
          >
            Contact
          </button>
        </div>
      </div>
      <div className="p-4 mt-2">
        <div className="bg-white text-black rounded-lg shadow-sm mb-6">
          <div className="p-3">
            <label className="block text-md font-medium mb-3 mt-3">
              Find your answer
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search guides.."
              className="block bg-gray-100 px-4 py-3 rounded-full w-full border-0 mb-3 font-light bg-opacity-50 placeholder-gray-450 focus:ring-black"
            />
          </div>

          {search.length > 2 && (
            <div className="pb-4">
              {searchResults
                .slice(0, searchExpanded ? searchResults.length : 3)
                .map((searchResult, index) => (
                  <div
                    className="p-4 border-b border-gray-200 hover:bg-gray-50 hover:bg-opacity-25 cursor-pointer"
                    key={index}
                    onClick={() =>
                      setCurrentView(`guides/${searchResult.slug}`)
                    }
                  >
                    <p className="text-md font-medium">{searchResult.title}</p>
                    <p className="font-light text-xs">{searchResult.excerpt}</p>
                  </div>
                ))}
              {searchResults.length > 3 && (
                <div
                  role="button"
                  className="bg-green-600 px-4 py-2 text-center text-white text-sm font-medium rounded-full mx-auto hover:bg-green-700 cursor-pointer w-2/4 mt-4"
                  onClick={() => setSearchExpanded(!searchExpanded)}
                >
                  {searchExpanded ? 'Show Less' : 'Show More'}
                </div>
              )}
            </div>
          )}
        </div>
        <div
          role="button"
          tabIndex={0}
          className="bg-white p-4 text-black rounded-lg shadow-sm mb-6 flex justify-start items-center text-md font-medium hover:bg-gray-100 cursor-pointer"
          onClick={() => setCurrentView('help_support')}
        >
          <WarningIcon className="block w-8 h-8 text-green-600 mr-3" /> Report a
          bug or technical issue
        </div>
      </div>
    </>
  );
};

export default HelpAttachmentLanding;
